import { useMediaQuery } from '@material-ui/core'
import { logAmplitudeEventAllowCustomers } from 'amplitude/amplitude'
import { Alert, BaseThemeType, Chip, styled, Typography } from 'opensolar-ui'
import { useEffect } from 'react'
import { CheckoutFinancingPricingOption, CheckoutFinancingPricingParams } from '../types'
import FinanceMarketplacePaymentsBreakdown from './FinanceMarketplacePaymentsBreakdown'
import MarketplaceCTA from './MarketplaceCTA'
import PricingOptionDetailsRow from './PricingOptionDetailsRow'

type PropTypes = {
  pricingOption: CheckoutFinancingPricingOption
  pricingParams: CheckoutFinancingPricingParams
  setHideDialog: (hide: boolean) => void
  closeRootDialog: () => void
  onApplicationStarted?: () => void
  fromOptimizedCheckout?: boolean
  rank?: number
  isAwaitingRefresh?: boolean
}

const MainContainer = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'center' : 'stretch',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.01);',
    border: `1px solid ${theme.palette.neutralGrey[400]}`,
    borderRadius: '10px',
    margin: '24px',
    position: 'relative',
  })
)

const LogoAndDescriptionContainer = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.neutralGrey[200],
    flex: 2,
    gap: isMobile ? '8px' : '24px',
    padding: '0px',
    width: isMobile ? '100%' : 'auto',
  })
)
const ProductTypeChipContainer = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  position: isMobile ? 'absolute' : 'relative',
  top: isMobile ? '-12px' : 'auto',
  left: isMobile ? '-8px' : 'auto',
}))
const LogoContainer = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  padding: isMobile ? '8px' : '0px',
}))
const ProductDescriptionContainer = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: isMobile ? 'flex-start' : 'center',
  alignItems: 'center',
  width: '100%',
  padding: isMobile ? '8px' : '0px',
}))
const ProductDescription = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  textAlign: 'center',
  color: theme.palette.neutralGrey[1000],
  fontWeight: 400,
  lineHeight: '16px',
}))
const RightColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  flex: 6,
}))

const ProductDetailsAndActions = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'center' : 'flex-start',
    padding: isMobile ? '8px 0px' : '0',
    borderRadius: '10px',
    gap: isMobile ? '8px' : '16px',
    backgroundColor: theme.palette.neutralGrey[100],
    width: '100%',
  })
)

const ProductDetails = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '100%',
  margin: '16px',
}))

const RepaymentContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
})

const ProductActions = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: isMobile ? 'center' : 'flex-end',
  alignItems: isMobile ? 'center' : 'flex-end',
  width: isMobile ? '100%' : 'auto',
  marginTop: isMobile ? '16px' : '0px',
  marginBottom: isMobile ? '16px' : '0px',
}))

const DesktopActionRow = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  justifyContent: isMobile ? 'flex-start' : 'flex-end',
  alignItems: 'center',
  width: isMobile ? 'auto' : '100%',
  marginBottom: isMobile ? '8px' : '16px',
  marginLeft: isMobile ? '8px' : '0px',
  marginRight: isMobile ? '8px' : '0px',
}))

const RepaymentTitle = styled(Typography)(() => ({
  fontWeight: 600,
}))

const RepaymentNumber = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.blue[800],
  marginLeft: '8px',
}))

const MarketplacePricingOptionRows = (props: PropTypes) => {
  const { pricingOption, pricingParams, setHideDialog, closeRootDialog, onApplicationStarted, fromOptimizedCheckout } =
    props
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))

  useEffect(() => {
    if (!props.isAwaitingRefresh) {
      logAmplitudeEventAllowCustomers('marketplace_pricing_option_row_rendered', {
        apr: props.pricingOption?.apr,
        monthly_payment: props?.pricingOption?.monthly_payment,
        total_repayable: props?.pricingOption?.total_repayable,
        term: props?.pricingOption?.term,
        rank: props?.rank,
        integration: pricingOption.lender_integration_name,
        product_title: pricingOption.product_description,
        product_type: pricingOption.sub_type_display_name,
      })
    }
  }, [
    props?.pricingOption?.apr,
    props?.pricingOption?.monthly_payment,
    props?.pricingOption?.total_repayable,
    props?.pricingOption?.term,
    props?.rank,
    props.isAwaitingRefresh,
  ])

  return (
    <MainContainer isMobile={isMobile}>
      <LogoAndDescriptionContainer isMobile={isMobile}>
        <ProductTypeChipContainer isMobile={isMobile}>
          {pricingOption.sub_type_display_name && (
            <Chip color="default" variant="filled" size="small" label={pricingOption.sub_type_display_name} />
          )}
        </ProductTypeChipContainer>
        <LogoContainer isMobile={isMobile}>
          <img src={pricingOption.lender_logo_url} height="50px" alt={pricingOption.lender_display_name} />
        </LogoContainer>
        <ProductDescriptionContainer isMobile={isMobile}>
          <ProductDescription textVariant="caption2">{pricingOption.product_description}</ProductDescription>
        </ProductDescriptionContainer>
        {pricingOption?.validation_messages?.map((message, i) => (
          <Alert severity={message?.message_type === 'error' ? 'error' : 'warning'} key={i}>
            {message?.message_body}
          </Alert>
        ))}
      </LogoAndDescriptionContainer>
      <RightColumn>
        <ProductDetailsAndActions isMobile={isMobile}>
          <ProductDetails isMobile={isMobile}>
            <DesktopActionRow isMobile={isMobile}>
              {!isMobile && (
                <ProductActions isMobile={isMobile}>
                  <MarketplaceCTA
                    rank={props.rank}
                    setHideDialog={setHideDialog}
                    pricingOption={pricingOption}
                    projectId={pricingParams?.project_id}
                    orgId={pricingParams.org_id}
                    systemUuid={pricingParams.system_uuid}
                    paymentOptionId={pricingParams.payment_option_id}
                    integration={pricingOption.lender_integration_name}
                    pricingParams={pricingParams}
                    closeRootDialog={closeRootDialog}
                    onApplicationStarted={onApplicationStarted}
                    fromOptimizedCheckout={fromOptimizedCheckout}
                  />
                </ProductActions>
              )}
            </DesktopActionRow>
            <PricingOptionDetailsRow pricingOption={pricingOption} />
          </ProductDetails>
        </ProductDetailsAndActions>
        <FinanceMarketplacePaymentsBreakdown pricingOption={pricingOption} rank={props.rank} />
        {isMobile && (
          <ProductActions isMobile={isMobile}>
            <MarketplaceCTA
              rank={props.rank}
              pricingOption={pricingOption}
              projectId={pricingParams?.project_id}
              systemUuid={pricingParams.system_uuid}
              paymentOptionId={pricingParams.payment_option_id}
              orgId={pricingParams.org_id}
              integration={pricingOption.lender_integration_name}
              pricingParams={pricingParams}
              setHideDialog={setHideDialog}
              closeRootDialog={closeRootDialog}
              onApplicationStarted={onApplicationStarted}
              fromOptimizedCheckout={fromOptimizedCheckout}
            />
          </ProductActions>
        )}
      </RightColumn>
    </MainContainer>
  )
}
export default MarketplacePricingOptionRows
